import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@material-ui/core";
import { Select } from "@ofqual/portal-components/components/Select";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";

import { Controller, useForm, useWatch } from "react-hook-form";
import { Error } from "../../../common/Error";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendMultipleMessage } from "../../../../redux/actions";
import { aoService, socStatusService } from "../../../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { SocMessageFilters } from "../../../../domain/SocMessageFilters";
import { authSelector } from "../../../../redux/selectors";
import { QueryParams } from "@ofqual/portal-core/core/types";

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: 15,
  },
}));

export const SendMultipleMessageDialog: React.FC<{
  onClose: () => void;
  open: boolean;
}> = ({ onClose, open }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useSelector(authSelector);

  const {
    handleSubmit,
    control,
    errors,
    setError,
    formState: { isSubmitted },
    clearErrors,
  } = useForm({
    defaultValues: {
      message: null,
      files: [],
      confirmSend: null,
      status: null,
      assignedToMe: null,
      noResponse: null,
      organisationId: [],
    },
  });

  const handleSendMessage = (data: {
    files: File[];
    message: string;
    status: number;
    assignedToMe: boolean;
    noResponse: boolean;
    organisationId: number[];
  }) => {
    const filters: SocMessageFilters = {
      status: data.status,
      assignedToMe: data.assignedToMe,
      noResponse: data.noResponse,
      organisationId: data.organisationId,
    };
    dispatch(sendMultipleMessage(data.files, data.message, filters));
    onClose();
  };

  const {
    confirmSend,
    sendToAll,
    organisationId,
    status,
    assignedToMe,
    noResponse,
  } = useWatch({
    control,
    name: [
      "confirmSend",
      "sendToAll",
      "organisationId",
      "status",
      "assignedToMe",
      "noResponse",
    ],
  });

  const [socStatusLookups] = useLookup(socStatusService);

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) => {
        const queryParams: QueryParams = {
          ...params,
          search: params?.search || params?.query,
        };
        return aoService.search(queryParams, { token });
      },
    }),
    [token]
  );

  const isFilterSelected =
    (organisationId && organisationId.length) ||
    status !== null ||
    noResponse ||
    assignedToMe;

  const isFilterRequired = isSubmitted && !sendToAll && !isFilterSelected;

  useEffect(() => {
    if (isFilterRequired)
      setError("noResponse", {
        message: "You must select a filter",
      });
    else clearErrors("noResponse");
  }, [setError, isFilterRequired, clearErrors]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle data-test="activity-title">Send Messages</DialogTitle>
      <Divider />
      <form>
        <DialogContent style={{ minHeight: "250px" }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                style={{ marginBottom: "2px", marginTop: "0px" }}
              >
                Select message filters or send to all SoCs
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="sendToAll"
                render={({ onChange }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        data-test="sendToAll-checkbox"
                        onChange={(e) => {
                          onChange(e.target.checked);
                        }}
                        disabled={
                          (organisationId && organisationId.length) ||
                          status !== null ||
                          noResponse ||
                          assignedToMe
                        }
                      />
                    }
                    label="Send to all SoC AOs"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                rules={{ required: true }}
                name="organisationId"
                render={({ onChange, ...props }) => (
                  <SearchField
                    {...props}
                    onChange={(e: any) => {
                      onChange(e);
                    }}
                    searchFieldVariant="multiple"
                    label="Filter by Awarding organisations"
                    datatest="filter-orgId"
                    service={mappedOrgLookupService}
                    disabled={
                      sendToAll || noResponse || status !== null || assignedToMe
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="status"
                as={
                  <Select
                    selectVariant="single"
                    label="Filter by status"
                    datatest="status-filter"
                    disabled={
                      sendToAll ||
                      (organisationId && organisationId.length) ||
                      assignedToMe
                    }
                  >
                    {socStatusLookups}
                  </Select>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="assignedToMe"
                render={({ onChange }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        data-test="assignedToMe-checkbox"
                        onChange={(e) => {
                          onChange(e.target.checked);
                        }}
                        disabled={
                          sendToAll ||
                          (organisationId && organisationId.length) ||
                          status !== null ||
                          noResponse
                        }
                      />
                    }
                    label="Filter by assigned to me"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="noResponse"
                render={({ onChange }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        data-test="noResponse-checkbox-message-modal"
                        onChange={(e) => {
                          onChange(e.target.checked);
                        }}
                        disabled={
                          sendToAll ||
                          (organisationId && organisationId.length) ||
                          assignedToMe
                        }
                      />
                    }
                    label="Filter by no response"
                  />
                )}
              />
            </Grid>
            {errors.noResponse && (
              <Grid item xs={12}>
                <Error data-test="filter-fields-error">
                  {errors.noResponse.message}
                </Error>
              </Grid>
            )}

            <Grid item xs={12}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="message"
                  rules={{ required: true }}
                  render={({ onChange, value }) => (
                    <MarkdownEditor
                      onChange={onChange}
                      value={value}
                      dataTest="create-message-text-field"
                    />
                  )}
                />
              </Grid>
              {errors.message && (
                <Error data-test="create-message-text-field-error">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="files"
                as={
                  <FileInput
                    multiple={true}
                    data-test="create-message-add-attachment-button"
                  />
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Grid item xs={12}>
            <Controller
              control={control}
              rules={{ required: true }}
              name="confirmSend"
              render={({ onChange }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      data-test="confirm-send-checkbox"
                      onChange={(e) => {
                        onChange(e.target.checked);
                      }}
                    />
                  }
                  label="I understand that this communication is directed at, and visible to, the awarding organisation *"
                  labelPlacement="start"
                  style={{ color: !confirmSend ? "red" : undefined }}
                />
              )}
            />
          </Grid>
          <Button
            onClick={onClose}
            color="primary"
            data-test="close-send-message-dialog"
          >
            Close
          </Button>
          <Button
            onClick={handleSubmit(handleSendMessage)}
            color="primary"
            data-test="send-message-button"
          >
            Send
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
